/**
 * Theme CSS Parser
 *
 * Extracts CSS custom properties from a raw @theme { } block.
 * Handles comments, blank lines, and tracks source line numbers.
 *
 * @module
 */
import { Effect } from "effect"
import type { ParsedTheme, ParsedToken } from "./types"
import { ThemeParseError } from "./types"

export const parseThemeCss = Effect.fn("parseThemeCss")(
  function* (raw: string) {
    // Find @theme keyword
    const themeIdx = raw.indexOf("@theme")
    if (themeIdx === -1) {
      return yield* new ThemeParseError({
        reason: "no-theme-block",
        message: "No @theme block found in CSS",
      })
    }

    // Find opening brace
    const braceStart = raw.indexOf("{", themeIdx)
    if (braceStart === -1) {
      return yield* new ThemeParseError({
        reason: "malformed-block",
        message: "@theme keyword found but no opening brace",
      })
    }

    // Find matching closing brace (handles theoretical nesting)
    let depth = 1
    let braceEnd = -1
    for (let i = braceStart + 1; i < raw.length; i++) {
      if (raw[i] === "{") depth++
      if (raw[i] === "}") depth--
      if (depth === 0) {
        braceEnd = i
        break
      }
    }

    if (braceEnd === -1) {
      return yield* new ThemeParseError({
        reason: "unclosed-block",
        message: "@theme block is missing its closing brace",
      })
    }

    // Line offset: how many lines precede the opening brace
    const linesBeforeBlock = raw.substring(0, braceStart + 1).split("\n").length

    // Extract and process block content line-by-line
    const blockContent = raw.substring(braceStart + 1, braceEnd)
    const lines = blockContent.split("\n")
    const tokens: ParsedToken[] = []
    let inComment = false

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i]

      // Skip lines inside block comments
      if (inComment) {
        const endIdx = line.indexOf("*/")
        if (endIdx === -1) continue
        line = line.substring(endIdx + 2)
        inComment = false
      }

      // Strip complete inline comments  /* ... */
      line = line.replace(/\/\*.*?\*\//g, "")

      // Handle comment that starts but doesn't end on this line
      const commentStart = line.indexOf("/*")
      if (commentStart !== -1) {
        line = line.substring(0, commentStart)
        inComment = true
      }

      // Match a custom property declaration: --name: value;
      const match = line.match(/^\s*(--([\w-]+))\s*:\s*(.+?)\s*;\s*$/)
      if (match) {
        tokens.push({
          name: match[1],
          value: match[3],
          line: linesBeforeBlock + i,
        })
      }
    }

    return {
      tokens,
      hasThemeBlock: true,
      rawLineCount: raw.split("\n").length,
    } satisfies ParsedTheme
  }
)
